// src/pages/HomePage.tsx

import { useEffect, useMemo, useRef, useState } from "react";
import { isPrerender } from "../utils/isPrerender";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ProductSchema from "../components/ProductSchema";
import { useAuth } from "../context/AuthContext";
import { useLoadScript } from "../hooks/useLoadScript";
import { supabase } from "../supabaseClient";

import AuthModal from "../components/AuthModal";
import TarotSection from "../components/TarotSection";
import FaqSection from "../components/FaqSection";
import FeaturesSection from "../components/FeaturesSection";
import HeroSection from "../components/HeroSection";
import KundliSection from "../components/KundliSection";
import GlobalShop from "../components/global/GlobalShop";
import GlobalTrustPoints from "../components/global/GlobalTrustPoints";
import GlobalUseCases from "../components/global/GlobalUseCases";
import MobileAppSection from "../components/MobileAppSection";
import CosmicBackground from "../components/CosmicBackground";
// import SubscriptionSection from "../components/SubscriptionSection";
import TestimonialsSection from "../components/TestimonialsSection";
import BlogStrip from "../components/BlogStrip";
import UnderstandAiAstrology from "../components/UnderstandAiAstrology";
import { usePricing } from "../context/PricingContext";

// Removed: `const stripePromise = loadStripe(...)`. It was declared here and
// never used — no Elements, no useStripe, no confirmCardPayment anywhere in
// this file. But loadStripe() injects Stripe's script as soon as the module
// evaluates, so every single homepage visitor was fetching js.stripe.com and
// carrying @stripe/stripe-js in the bundle for a payment provider this page
// does not use. Payments here run on Razorpay (see isRazorpayLoaded below).
const PENDING_KUNDLI_DETAILS_KEY = "pendingKundliDetails";

declare global {
  interface Window {
    Razorpay: any;
  }
}

/* Kept as a flag rather than a deletion, so restoring the homepage shop
   slider is a one-word change and the intent stays visible in the file.

   Back on. Worth knowing this flag was never part of the PayU freeze: it was
   turned off on 2026-08-17 (1c517ec) as a layout call, two days BEFORE the
   store itself was gated. So turning SHOW_STORE back on did not restore the
   homepage slider, which is why the homepage looked unchanged. */
const SHOW_HOMEPAGE_SHOP = true;

export default function HomePage() {
  const navigate = useNavigate();
  // --- 1. Get userProfiles from useAuth to perform the check ---
  const {
    user,
    currency,
    userCountry,
    signInWithGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    planTier,
    checkingStatus,
    pricingVariant,
    refreshUserStatus,
    userProfiles, // Added this
  } = useAuth();

  // Remedies store is India-only (INR + India geo), matching the app + navbar.
  //
  // BOTH signals default to India when unresolved. userCountry already did;
  // currency did not, and that asymmetry is why the shop slider never showed:
  // currency is null until the geo probe answers (and stays null when there is
  // no Geoapify key), so `currency === "INR"` was false and the whole gate
  // failed shut. Same failure the navbar Shop link hit before it was ungated
  // (see a519364). Defaulting both keeps the India-only rule intact while
  // making "not answered yet" behave like the India case rather than the
  // foreign one.
  const isIndiaShopper =
    (currency ?? "INR").toUpperCase() === "INR" &&
    (userCountry || "IN").toUpperCase() === "IN";

  const { variantIsLoading } = usePricing();

  const { variant } = usePricing();

  const isRazorpayLoaded = useLoadScript(
    "https://checkout.razorpay.com/v1/checkout.js",
  );

  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [activePlanId, setActivePlanId] = useState<number | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const subscriptionRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const faqRef = useRef<HTMLElement>(null);
  const { showSubscriptions } = usePricing();

  const phoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestPhoneOtp,
      verifyOtp: verifyPhoneOtp,
    }),
    [requestPhoneOtp, verifyPhoneOtp],
  );

  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp],
  );

  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: (payload: {
        accessToken: string;
        phone: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }) => {
        return verifyMsg91Otp({
          ...payload,
          variant: pricingVariant,
        });
      },
    }),
    [verifyMsg91Otp, pricingVariant],
  );

  // ---- Mark this session as the "main" experience (resets any market mode) ----
  useEffect(() => {
    try {
      sessionStorage.setItem("auraMode", "main");
    } catch {
      /* sessionStorage unavailable */
    }
  }, []);

  useEffect(() => {
    /* react-snap has no Supabase session, so this fetch always failed during
       the crawl and could bake "Could not load subscription plans." into the
       HOMEPAGE snapshot ("/" renders this component; /pricing has its own
       copy of the same guard in PricingDetailsQuickRechargePage). Real
       visitors fetch as before. */
    if (isPrerender()) return;
    if (checkingStatus || !currency || variantIsLoading) return;

    // Wait until currency is determined (it might be null briefly on initial load)
    // if (!currency) return;

    const determineLocationAndFetchPlans = async () => {
      setLoading(true);
      setError(null);

      // LOGIC SIMPLIFIED: We derive country purely from the Context Currency now
      let countryCode = "US";
      const upperCurr = currency.toUpperCase();

      if (upperCurr === "INR") countryCode = "IN";
      else if (upperCurr === "GBP") countryCode = "GB";
      else if (upperCurr === "AED") countryCode = "AE";
      else if (upperCurr === "EUR")
        countryCode = "DE"; // Generic EU
      else countryCode = "US";

      console.log(
        `[HomePage] Fetching plans for Currency: ${currency}, Country: ${countryCode}, Variant: ${pricingVariant}`,
      );
      try {
        const { data, error: plansError } = await supabase.functions.invoke(
          "get-location-and-plans",
          { body: { country: countryCode, variant: pricingVariant } }, // <-- PASS THE VARIANT
        );

        if (plansError) throw new Error(plansError.message);
        setPlans(data.plans || []);
      } catch (plansError) {
        console.error("Error fetching plans:", plansError);
        setError("Could not load subscription plans.");
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    determineLocationAndFetchPlans();

    // Add currency to dependency array so it refetches if currency changes
  }, [user, currency, checkingStatus, pricingVariant, variantIsLoading]);

  // This useEffect for scrolling remains unchanged
  useEffect(() => {
    if (location.hash === "#pricing" && subscriptionRef.current) {
      subscriptionRef.current.scrollIntoView({ behavior: "smooth" });
    }
    if (location.hash === "#faq" && faqRef.current) {
      faqRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location, loading]);

  // This useEffect now contains the fix for the loophole
  useEffect(() => {
    // Run only when auth status is fully resolved
    if (user && !checkingStatus && userProfiles) {
      // Added userProfiles check
      const pendingDetailsJSON = sessionStorage.getItem(
        PENDING_KUNDLI_DETAILS_KEY,
      );

      if (pendingDetailsJSON) {
        // Multiple Kundli profiles are allowed for everyone — no subscription
        // required — so we proceed straight to creating the profile.
        sessionStorage.removeItem(PENDING_KUNDLI_DETAILS_KEY);

        const createPendingProfile = async () => {
          try {
            const details = JSON.parse(pendingDetailsJSON);

            // The two-step database insert logic
            const { data: profileData, error: profileError } = await supabase
              .from("user_profiles")
              .insert({ name: details.name, user_id: user.id })
              .select()
              .single();

            if (profileError) throw profileError;

            const { error: birthDetailsError } = await supabase
              .from("user_birth_details")
              .insert({
                profile_id: profileData.id,
                date_of_birth: details.date_of_birth,
                time_of_birth: details.time_of_birth,
                gender: details.gender,
                birth_place: details.birth_place,
                birth_lat: details.birth_lat,
                birth_lng: details.birth_lng,
                birth_timezone: details.birth_timezone,
              });

            if (birthDetailsError) throw birthDetailsError;

            await refreshUserStatus();
            navigate("/reports");
          } catch (error) {
            console.error("Failed to save pending Kundli details:", error);
            alert(
              "There was an issue creating your Kundli profile. Please try again from the homepage.",
            );
          }
        };

        createPendingProfile();
      }
    }
    // Dependency array is updated to include the new checks
  }, [
    user,
    checkingStatus,
    userProfiles,
    planTier,
    navigate,
    refreshUserStatus,
  ]);


  // Automatic re-registration for users who already granted permission
  useEffect(() => {
    const reRegisterExistingUsers = async () => {
      // @ts-ignore - CleverTap types not available
      const clevertap = (window as any).clevertap;

      // Check if already re-registered
      const hasReRegistered = localStorage.getItem("ct_push_reregistered");
      if (hasReRegistered) {
        return;
      }

      // Only for users who already have permission granted
      if (
        typeof Notification === "undefined" ||
        Notification.permission !== "granted"
      ) {
        return;
      }

      // Wait for CleverTap to load
      if (!clevertap?.notifications?.push) {
        console.log("[Re-registration] CleverTap not ready yet, skipping...");
        return;
      }

      try {
        console.log(
          "[Re-registration] Starting silent re-registration for existing user...",
        );

        // Silently register push token with CleverTap
        await clevertap?.notifications.push({
          titleText: "Notifications Updated",
          bodyText: "Your notification settings have been improved.",
          skipDialog: true,
          serviceWorkerPath: "/clevertap_sw.js",
          vapidPublicKey:
            "BBP6tWBZCnyNnN39wXWHaKJP_K2WbDM4034HIr2Md_aTb36mqkkldoFP_rxh55v-_G1rIxSZE0Q6rEMXIT5pVuo",
        });

        // Mark as re-registered
        localStorage.setItem("ct_push_reregistered", "true");
        console.log(
          "✅ [Re-registration] User successfully re-registered for push notifications",
        );

        // Update profile
        if (clevertap?.profile?.push) {
          clevertap?.profile.push({
            Site: {
              push_enabled: true,
              re_registered: true,
              re_registered_at: new Date().toISOString(),
            },
          });
        }
      } catch (error) {
        console.warn("[Re-registration] Failed to re-register user:", error);
        // Don't set reregistered flag on failure, will retry on next page load
      }
    };

    // Wait 5 seconds after page load to ensure CleverTap is ready
    const timer = setTimeout(() => {
      reRegisterExistingUsers();
    }, 5000);

    return () => clearTimeout(timer);
  }, []); // Run once on mount

  // // Updated handleSubscribe function to use direct subscription creation
  // const handleSubscribe = async (priceId: number | string) => {
  //   if (!user) {
  //     setIsAuthModalOpen(true);
  //     return;
  //   }
  //   setIsSubscribing(true);
  //   setActivePlanId(typeof priceId === "string" ? parseInt(priceId) : priceId);

  //   try {
  //     // Find the plan from the plans array to get the correct amount and plan type
  //     const selectedPlan = plans.find((plan) => plan.price.id === priceId);
  //     if (!selectedPlan) {
  //       throw new Error("Selected plan not found");
  //     }

  //     const planType = selectedPlan.interval === "month" ? "monthly" : "yearly";
  //     const amount = selectedPlan.price.amount; // Use the actual amount from the plan

  //     console.log("Selected plan:", selectedPlan);
  //     console.log("Plan type:", planType);
  //     console.log("Amount:", amount);

  //     // Direct Razorpay integration
  //     if (!isRazorpayLoaded)
  //       throw new Error("Razorpay script is not yet loaded.");

  //     const options = {
  //       key: process.env.REACT_APP_RAZORPAY_KEY_ID,
  //       amount: amount,
  //       currency: selectedPlan.price.currency.toUpperCase(), // Use currency from location-based plan
  //       name: "AuraAI Astrology",
  //       description: `${planType === "monthly" ? "Monthly" : "Yearly"} Subscription`,
  //       handler: async (response: any) => {
  //         console.log("💳 Payment successful:", response);

  //         try {
  //           // Use UPSERT for reliable insert-or-update
  //           // --- AFTER THE FIX (CORRECT) ---

  //           // Get current user state first
  //           const { data: existingUser, error: fetchError } = await supabase
  //             .from("users")
  //             .select("coin_balance") // Only need coin_balance
  //             .eq("id", user.id)
  //             .single();

  //           if (fetchError && fetchError.code !== "PGRST116") {
  //             // Ignore "no rows found" error for new users
  //             throw fetchError;
  //           }

  //           // --- CORRECT COIN CALCULATION LOGIC ---
  //           // 1. Get the number of coins to add from the selected plan's entitlements.
  //           const coinsToAdd =
  //             selectedPlan.entitlements?.coins_granted_on_subscription || 0;

  //           // 2. Get the user's current coin balance (defaults to 0 if they don't exist yet).
  //           const currentCoins = existingUser?.coin_balance || 0;

  //           // 3. Calculate the new total.
  //           const newCoinBalance = currentCoins + coinsToAdd;

  //           // Use UPSERT for reliable insert-or-update
  //           const { data: result, error: upsertError } = await supabase
  //             .from("users")
  //             .upsert(
  //               {
  //                 id: user.id,
  //                 email: user.email,
  //                 plan_tier: planType,
  //                 subscription_status: "active",
  //                 subscription_start_date: new Date().toISOString(),
  //                 subscription_end_date: new Date(
  //                   Date.now() +
  //                     (planType === "monthly" ? 30 : 365) * 24 * 60 * 60 * 1000,
  //                 ).toISOString(),
  //                 coin_balance: newCoinBalance, // Use the new calculated balance
  //               },
  //               {
  //                 onConflict: "id",
  //               },
  //             )
  //             .select();
  //           if (upsertError) {
  //             console.error("❌ Upsert error:", upsertError);
  //             alert(
  //               "Payment successful but subscription update failed: " +
  //                 upsertError.message,
  //             );
  //             return;
  //           }

  //           console.log("✅ Subscription updated via upsert:", result);

  //           // Verify the update
  //           const { data: verifyUser, error: verifyError } = await supabase
  //             .from("users")
  //             .select("*")
  //             .eq("id", user.id)
  //             .single();

  //           console.log("✅ Verified user state:", verifyUser);

  //           if (verifyUser?.subscription_status === "active") {
  //             console.log("🎉 Subscription confirmed active!");
  //             // Refresh AuthContext before navigating
  //             await refreshUserStatus();
  //             navigate("/payment-success");
  //           } else {
  //             console.error(
  //               "⚠️ Subscription status not active:",
  //               verifyUser?.subscription_status,
  //             );
  //             alert(
  //               "Payment successful but subscription status is: " +
  //                 verifyUser?.subscription_status,
  //             );
  //           }
  //         } catch (error) {
  //           console.error("❌ Payment handler error:", error);
  //           alert(
  //             "Payment successful but there was an error: " +
  //               (error as Error).message,
  //           );
  //         }
  //       },
  //       prefill: {
  //         email: user.email,
  //         name: user.user_metadata?.full_name || user.email?.split("@")[0],
  //       },
  //       theme: { color: "#3399cc" },
  //       modal: {
  //         ondismiss: function () {
  //           setIsSubscribing(false);
  //           setActivePlanId(null);
  //         },
  //       },
  //     };

  //     const rzp = new window.Razorpay(options);
  //     rzp.on("payment.failed", (response: any) => {
  //       alert("Payment failed. Please try again.");
  //       console.error("Payment failed:", response.error);
  //       setIsSubscribing(false);
  //       setActivePlanId(null);
  //     });
  //     rzp.open();
  //   } catch (err: any) {
  //     console.error("Subscription Error:", err);
  //     alert(`Error: ${err.message}`);
  //   } finally {
  //     // Note: We don't set loading to false here because Razorpay modal handles it
  //   }
  // };

  // The returned JSX remains unchanged
  return (
    <main className="relative" style={{ position: "relative", isolation: "isolate" }}>
      <ProductSchema />
      <Helmet>
        <title>AI Astrology Free Chat: First Question Free | Aura AI</title>
        <meta
          name="description"
          content="Chat with an AI Vedic astrologer 24x7. First question free, no card. Get your free kundli and answers on love, career and marriage from your own birth chart."
        />
        <link rel="canonical" href="https://astroaura.ai/" />
        <meta
          property="og:title"
          content="AI Astrology Free Chat: First Question Free | Aura AI"
        />
        <meta
          property="og:description"
          content="Chat with an AI Vedic astrologer 24x7. First question free, no card. Get your free kundli and answers on love, career and marriage from your own birth chart."
        />
        <meta property="og:url" content="https://astroaura.ai/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://astroaura.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI Astrology Free Chat: First Question Free | Aura AI"
        />
        <meta
          name="twitter:description"
          content="Chat with an AI Vedic astrologer 24x7. First question free, no card. Get your free kundli and answers on love, career and marriage from your own birth chart."
        />
        <meta name="twitter:image" content="https://astroaura.ai/og-image.png" />
      </Helmet>
      <CosmicBackground />

      {/* Web push permission banner removed per product decision (was the
          CleverTap notification prompt). */}

      {/* WRAPPER: Force relative positioning */}
      <div style={{ position: "relative", width: "100%", zIndex: 1 }}>
        <HeroSection
          user={user}
          onGoogleSignIn={signInWithGoogle}
          phoneAuth={{
            requestOtp: requestPhoneOtp,
            verifyOtp: verifyPhoneOtp,
          }}
          msg91Auth={msg91AuthHandlers}
        />

        {/* THE FIX (FORCE VISIBLE):
            1. zIndex: 50 -> Forces it above the Hero image.
            2. height: 150px -> Taller gradient for a smoother fade.
            3. background: Hardcoded linear-gradient to your maroon color (#61072B).
        */}
        {/* <div
          style={{
            position: "absolute",
            bottom: "-1px",
            left: "0",
            width: "100%",
            // height: "10px",
            height: "150px",
            // background: 'linear-gradient(to bottom, rgba(97, 7, 43, 0) 0%, #61072B 100%)',
            // background: "linear-gradient(to bottom, #2D0147 0%, #200034 100%)",
            background:
              "linear-gradient(to bottom, rgba(76, 6, 95, 0) 0%, rgb(95, 6, 83) 100%)",

            zIndex: 50,
            pointerEvents: "none",
          }}
        /> */}
        {/* <div
          style={{
            position: "absolute",
            bottom: "0",
            left: "0",
            width: "100%",
            height: "150px",
            background:
              "linear-gradient(to bottom, rgba(97, 7, 43, 0) 0%, #61072B 100%)",
            zIndex: 30,
            pointerEvents: "none",
          }}
        /> */}
      </div>

      <div style={{ backgroundColor: "transparent", marginTop: "-1px", position: "relative", zIndex: 1 }}>
        <FeaturesSection withImages />
      </div>

      {/* {!checkingStatus && (
        <SubscriptionSection
          ref={subscriptionRef}
          plans={plans}
          isLoading={loading}
          error={error}
          onSubscribe={handleSubscribe}
          isSubscribing={isSubscribing}
          activePlanId={activePlanId}
          currentUserPlanId={planTier}
        />
      )} */}
      <div style={{ backgroundColor: "transparent", marginTop: "-1px", position: "relative", zIndex: 1 }}>
        <TarotSection onRequestLogin={() => setIsAuthModalOpen(true)} />

        <KundliSection />
      </div>

      {/* WHY PEOPLE TRUST AURA AI */}
      <GlobalTrustPoints />

      {/* ASK AURA AI ABOUT ANYTHING */}
      <GlobalUseCases />

      {/* ECOMMERCE SLIDER — India-only (we don't sell remedies abroad).
          Originally the fifth thing on the page, directly under the feature
          cards, which put the shop ahead of the product. It now sits here,
          above the reviews: still well below the fold so AI astrology leads,
          but the testimonials land straight after it, which is the right order
          for anyone who has just scrolled a row of products. */}
      {/* India-only: isIndiaShopper needs INR pricing AND an India geo signal,
          so this is absent from the prerendered HTML (currency is not resolved
          at snapshot time) and appears client-side for Indian visitors. */}
      {SHOW_HOMEPAGE_SHOP && isIndiaShopper && (
        <GlobalShop
          onShopAll={() =>
            window.open("https://astroaura.market", "_blank", "noopener")
          }
        />
      )}

      <div style={{ backgroundColor: "transparent", marginTop: "-1px", position: "relative", zIndex: 1 }}>
        <TestimonialsSection />
      </div>

      {/* Four written articles, directly above the app block. Until now the
          only internal route to /blog was the navbar link. */}
      <BlogStrip />

      <div style={{ backgroundColor: "transparent", marginTop: "-35px", position: "relative", zIndex: 1 }}>
        <MobileAppSection />
      </div>

      {/* Long-form AI-astrology explainer. Sits directly above the FAQ: both
          are the reading blocks of the page, and the FAQPage schema reads as a
          continuation of it. */}
      <UnderstandAiAstrology />

      <div style={{ backgroundColor: "transparent", marginTop: "-1px", position: "relative", zIndex: 1 }}>
        <section ref={faqRef}>
          {/* Homepage-only heading. "Cosmic Questions Answered" is on-brand but
              carries no search language, and this H2 sits directly above the
              FAQPage schema, which is the block most likely to be lifted into
              an AI Overview or a People Also Ask result. /market and /global
              keep the original wording so the three pages stay distinct. */}
          <FaqSection
            heading="Free AI Astrology Chat: Your Questions Answered"
            subheading="What is free, how the AI reads your kundli, and what it costs after that"
          />
        </section>
      </div>
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
    </main>
  );
}
